import React from 'react';
import { CheckCircle2, XCircle, Zap, MousePointer } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { QUESTION_TYPE_REGISTRY } from '../../../config/questionTypeRegistry';
const TYPE_DESCRIPTIONS: Record<string, string> = {
  multiple_choice: 'Standaard meerkeuze met 4 opties. Opties worden geschud.',
  cijferend_optellen: 'Kolom-optelling met carry-over invoer.',
  cijferend_aftrekken: 'Kolom-aftrekking met borrow invoer.',
  clock: 'Analoge klok instellen op een doeltijd.',
  money: 'Geldbedrag samenstellen uit munten en biljetten.',
  fill_in: 'Vrij tekst- of getalinvoer met geaccepteerde antwoorden.',
  ordering: 'Items in de juiste volgorde slepen.',
  matching: 'Paren koppelen (links ↔ rechts).',
  drag_drop_fill: 'Woorden of getallen naar lege plekken slepen.',
  table_reading: 'Juiste cel in een tabel selecteren.',
  number_sequence: 'Ontbrekende waarden in een reeks invullen.',
  ratio_table: 'Verhoudingstabel aanvullen met verborgen cellen.',
  text_highlight: 'Correct tekstsegment markeren.',
  bar_chart: 'Juiste staaf in een staafgrafiek selecteren.',
  line_chart: 'Juiste punt in een lijngrafiek selecteren.',
  schedule_reading: 'Juiste cel in een rooster/schema selecteren.',
  pie_chart: 'Juiste segment in een cirkeldiagram selecteren.'
};
const TYPE_MODULES: Record<string, string[]> = {
  multiple_choice: ['Alle modules'],
  cijferend_optellen: ['GET'],
  cijferend_aftrekken: ['GET'],
  clock: ['TGL'],
  money: ['TGL'],
  fill_in: ['GET', 'MET', 'SWW', 'SPR', 'TAA'],
  ordering: ['TAA', 'STU'],
  matching: ['TAA', 'SWW'],
  drag_drop_fill: ['TAA', 'SPR'],
  table_reading: ['MET', 'TGL', 'STU'],
  number_sequence: ['GET', 'MET'],
  ratio_table: ['GET', 'MET'],
  text_highlight: ['TAA', 'STU'],
  bar_chart: ['GET', 'STU'],
  line_chart: ['GET', 'STU'],
  schedule_reading: ['TGL', 'STU'],
  pie_chart: ['STU']
};
export function QuestionTypesPanel() {
  const types = Object.values(QUESTION_TYPE_REGISTRY);
  const interactiveCount = types.filter(t => t.isInteractive).length;
  const citoCount = types.filter(t => t.isCitoEligible).length;
  return <div className="space-y-6" data-id="element-2325">
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-4" data-id="element-2326">
        <Card className="p-4 bg-indigo-50 border-indigo-200" data-id="element-2327">
          <div className="text-3xl font-black text-indigo-700" data-id="element-2328">
            {types.length}
          </div>
          <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mt-0.5" data-id="element-2329">
            Question Types
          </div>
        </Card>
        <Card className="p-4 bg-blue-50 border-blue-200" data-id="element-2330">
          <div className="text-3xl font-black text-blue-700" data-id="element-2331">
            {interactiveCount}
          </div>
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mt-0.5" data-id="element-2332">
            Interactive
          </div>
        </Card>
        <Card className="p-4 bg-emerald-50 border-emerald-200" data-id="element-2333">
          <div className="text-3xl font-black text-emerald-700" data-id="element-2334">
            {citoCount}
          </div>
          <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mt-0.5" data-id="element-2335">
            CITO Eligible
          </div>
        </Card>
      </div>

      {/* Type grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2336">
        {types.map(config => {
        const description = TYPE_DESCRIPTIONS[config.type] ?? '—';
        const modules = TYPE_MODULES[config.type] ?? [];
        return <div key={config.type} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3" data-id="element-2337">
              {/* Header */}
              <div className="flex items-start justify-between gap-2" data-id="element-2338">
                <div data-id="element-2339">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-2340">
                    {config.label}
                  </div>
                  <code className="text-[11px] text-slate-400 font-mono" data-id="element-2341">
                    {config.type}
                  </code>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0" data-id="element-2342">
                  {config.isInteractive ? <Badge variant="info" size="sm" className="flex items-center gap-1" data-id="element-2343">
                      <MousePointer className="w-2.5 h-2.5" data-id="element-2344" />
                      Interactive
                    </Badge> : <Badge variant="neutral" size="sm" className="flex items-center gap-1" data-id="element-2345">
                      <MousePointer className="w-2.5 h-2.5 opacity-40" data-id="element-2346" />
                      Static
                    </Badge>}
                  {config.isCitoEligible && <Badge variant="success" size="sm" className="flex items-center gap-1" data-id="element-2347">
                      <Zap className="w-2.5 h-2.5" data-id="element-2348" />
                      CITO
                    </Badge>}
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-slate-500 leading-relaxed" data-id="element-2349">
                {description}
              </p>

              {/* Footer: modules + shuffle */}
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100" data-id="element-2350">
                <div className="flex flex-wrap gap-1" data-id="element-2351">
                  {modules.map(mod => <span key={mod} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-id="element-2352">
                      {mod}
                    </span>)}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-slate-400 shrink-0" data-id="element-2353">
                  {config.shouldShuffleOptions ? <CheckCircle2 className="w-3 h-3 text-emerald-500" data-id="element-2354" /> : <XCircle className="w-3 h-3 text-slate-300" data-id="element-2355" />}
                  shuffle
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}